import React, { useState } from 'react';
import {
  Animated,
  Pressable,
  View,
  Image,
  ActivityIndicator,
} from 'react-native';
import { ScrollView } from 'react-native-gesture-handler';

import { getTemplate, isWordItem } from '@/api/services/templates';
import { Icon } from '@/components/Icon';
import { IconName } from '@/components/NeolexonIcon/Icons/types';
import { Pill } from '@/components/Pill';
import { PressableNeolexon } from '@/components/PressableNeolexon';
import { Button } from '@/components/ui/therapist/Button';
import Card from '@/components/ui/therapist/Card';
import { Modal } from '@/components/ui/therapist/Modal';
import RadioButton from '@/components/ui/therapist/RadioButton';
import { Text } from '@/components/ui/therapist/Text';
import colors from '@/config/colors';
import { useRotateAnimation } from '@/hooks/useRotateAnimation';

import { TemplateItem, Template as TemplateType } from './types';
import { styles } from './styles';

type TemplateProps = {
  template: TemplateType;
  footerComponent?: () => React.ReactNode;
  onSelect?: (template: TemplateType) => void;
  isSelected?: boolean;
};

const setConfig = {
  Word: {
    icon: 'letter',
    label: 'Wörterset',
  },
  Sentence: {
    icon: 'sentence',
    label: 'Sätzeset',
  },
  Text: {
    icon: 'book',
    label: 'Texteset',
  },
};

const difficultyIconMap = {
  easy: {
    icon: 'weightEasy' as IconName,
    label: 'Leicht',
  },
  medium: {
    icon: 'weightMedium' as IconName,
    label: 'Mittel',
  },
  hard: {
    icon: 'weightHard' as IconName,
    label: 'Schwer',
  },
};

export const Template: React.FC<TemplateProps> = ({
  footerComponent,
  template,
  onSelect,
  isSelected = false,
}) => {
  const [expanded, setExpanded] = useState<boolean>(false);
  const [openDetailModal, setOpenDetailModal] = useState<boolean>(false);
  const [templateDetails, setTemplateDetails] = useState<TemplateItem[] | null>(
    null,
  );

  const rotate = useRotateAnimation(expanded, 200);

  const config = setConfig[template.setType];

  const handleSeeDetails = async () => {
    setOpenDetailModal(true);

    const { items = [] } = await getTemplate(template.id);
    setTemplateDetails(items);
  };

  const renderTemplateItem = (item: TemplateItem, index: number) => {
    const object = isWordItem(item) ? item.word : item.sentence;
    return (
      <View
        key={index}
        style={[styles.itemRow, index !== 0 ? styles.itemRowBorder : null]}
      >
        <Image
          resizeMode="contain"
          style={styles.itemImage}
          source={{
            uri: object.pictures[0].appimage_url,
          }}
        />
        <Text> {object.print}</Text>
      </View>
    );
  };

  return (
    <>
      <Card
        styleBody={[
          styles.cardBody,
          isSelected ? { borderColor: colors.newUI.therapist.blue60 } : null,
        ]}
      >
        <Pressable onPress={() => (onSelect ? onSelect(template) : null)}>
          <View style={styles.templateHeaderWrapper}>
            <View
              style={
                onSelect
                  ? {
                      flexDirection: 'row',
                      alignItems: 'flex-start',
                      gap: 8,
                    }
                  : null
              }
            >
              {onSelect ? (
                <RadioButton
                  onPress={() => onSelect(template)}
                  isSelected={isSelected}
                />
              ) : null}
              <View style={{ flex: 1 }}>
                <Text style={styles.title}>{template.name}</Text>
                <Pressable
                  onPress={() => setExpanded((p) => !p)}
                  style={styles.detailsRow}
                >
                  <Text style={styles.detailsText}>Details einblenden</Text>
                  <Animated.View style={{ transform: [{ rotate }] }}>
                    <Icon
                      name="chevronDown"
                      size={24}
                      color={colors.newUI.therapist.primaryBlue80}
                    />
                  </Animated.View>
                </Pressable>
                {expanded ? (
                  <View style={[styles.descriptionWrapper]}>
                    <Text
                      style={{ color: colors.newUI.therapist.primaryBlue80 }}
                    >
                      {template.description}
                    </Text>
                    <PressableNeolexon
                      onPress={handleSeeDetails}
                      style={{
                        flexDirection: 'row',
                        alignItems: 'center',
                        gap: 4,
                      }}
                    >
                      <Icon name="eye" size={16} />
                      <Text>Vorschau</Text>
                    </PressableNeolexon>
                  </View>
                ) : null}
              </View>
            </View>
          </View>
        </Pressable>
        <View style={styles.pillWrapper}>
          <Pill
            label={config.label}
            icon={config.icon as IconName}
            isNeolexon={template.isNeolexonTemplate}
            iconSize={22}
          />
          <Pill
            label={difficultyIconMap[template.difficulty].label}
            icon={difficultyIconMap[template.difficulty].icon}
            isNeolexon={false}
            iconSize={22}
          />
        </View>
        {footerComponent?.() ? (
          <View style={styles.actionsRow}>{footerComponent()}</View>
        ) : null}
      </Card>

      <Modal
        title={template.name}
        isVisible={openDetailModal}
        confirmActionButton={
          <Button
            label="Schließen"
            look="secondary"
            onPress={() => setOpenDetailModal(false)}
          />
        }
        onClose={() => setOpenDetailModal(false)}
      >
        <ScrollView style={{ minWidth: 500 }}>
          {templateDetails ? (
            templateDetails.map((item, index) =>
              renderTemplateItem(item, index),
            )
          ) : (
            <ActivityIndicator
              size="large"
              color={colors.newUI.therapist.primaryBlue80}
            />
          )}
        </ScrollView>
      </Modal>
    </>
  );
};
